/**
 * Fronta práce — klasifikace budov / rozvaděčů a „Chybí“.
 *
 * Tři množiny (záfuk / provaření / milníkové fronty + rozvaděče):
 *   • matched — připravené k práci (aktuální fronta)
 *   • missing (Chybí) — milník ještě není dosažen a objekt není v matched
 *   • done (Hotovo) — milník už dosažen → ani matched, ani Chybí (UI sekce Hotovo)
 *
 * Fail-closed:
 *   • Záfuk / provaření: neznámý status → budova do fronty nepatří.
 *   • Záfuk / provaření: fáze Zrušený (BUDOVA.1.PHASE.1) → hotovo (mimo matched i Chybí).
 *   • Akvizice: jen pozitivní akvizice (Uzavřeno / podepsaný souhlas); neznámý stav → mimo tabulku (Chybí).
 *   • Milníkové záložky: mimo type/HP scope → mimo matched i Chybí.
 */

import {
  ACQUISITION_MAP_COLORS,
  ACQUISITION_SOUHLAS_SMLUVA,
  isAcquisitionClosed,
  isAkviziceDrawingRejectedPair,
  isAkviziceDrawingSignedPair,
  normalizeAcquisitionText,
} from '../constants/acquisition-report.constants';
import {
  isBudovaBuildingType,
  type BudovaBuildingType,
} from '../constants/object-workflow/budova-building-type.constants';
import {
  budovaRequiresSvarPhase,
  isBudovaNepripojenyPhase,
  isMilestoneComplete,
  type BudovaMilestonesFlags,
} from '../constants/object-workflow/budova-milestone-engine';
import { PHASE_CODE } from '../constants/object-workflow/phase-codes';
import {
  getNextPhaseCodes,
  hasReachedWorkflowMilestone,
  isSamePhase,
} from '../constants/object-workflow/workflow-model';
import { isBudovaZrusenaPhase } from './budova-pripojeni-exclusion.util';
import { isMsoHp, isVsoHp } from './budova-hp-display.util';

export type WorkQueueTabId =
  | 'budovy'
  | 'k-zavrtu'
  | 'k-odboceni'
  | 'vr'
  | 'digitalni-cesta'
  | 'akvizice'
  | 'zafuk'
  | 'provareni'
  | 'rozvadec-postaveni'
  | 'rozvadec-zafuk'
  | 'rozvadec-provareni';

export const WORK_QUEUE_TAB_IDS: readonly WorkQueueTabId[] = [
  'budovy',
  'k-zavrtu',
  'k-odboceni',
  'vr',
  'digitalni-cesta',
  'akvizice',
  'zafuk',
  'provareni',
  'rozvadec-postaveni',
  'rozvadec-zafuk',
  'rozvadec-provareni',
] as const;

export const WORK_QUEUE_TAB_LABELS: Record<WorkQueueTabId, string> = {
  budovy: 'Budovy',
  'k-zavrtu': 'Budovy k závrtu',
  'k-odboceni': 'Budovy k odbočení',
  /** Stejný český název jako podtitul fronty (milník Zřízené VR = vertikální rozvody). */
  vr: 'Vertikální rozvody',
  'digitalni-cesta': 'Digitální cesta',
  akvizice: 'Akvizice',
  zafuk: 'Budovy k zafouknutí',
  provareni: 'Budovy k provaření',
  'rozvadec-postaveni': 'Rozvaděče k postavení',
  'rozvadec-zafuk': 'Rozvaděče k zafouknutí',
  'rozvadec-provareni': 'Rozvaděče k provaření',
};

/** Výchozí záložka Fronta práce (dříve HP). */
export const WORK_QUEUE_DEFAULT_TAB: WorkQueueTabId = 'budovy';

/** Záložky pracující nad rozvaděči (ROZVADEC) místo budov. */
export const WORK_QUEUE_ROZVADEC_TAB_IDS: readonly WorkQueueTabId[] = [
  'rozvadec-postaveni',
  'rozvadec-zafuk',
  'rozvadec-provareni',
] as const;

/** Záložky s milníkovým filtrem (Chybí jen v type/HP scope). */
export const WORK_QUEUE_MILESTONE_TAB_IDS: readonly WorkQueueTabId[] = [
  'k-zavrtu',
  'k-odboceni',
  'vr',
  'digitalni-cesta',
] as const;

export function isRozvadecWorkQueueTab(tab: WorkQueueTabId): boolean {
  return (WORK_QUEUE_ROZVADEC_TAB_IDS as readonly string[]).includes(tab);
}

/** Záložky budov se sloupcem DP (zdrojový rozvaděč) a seskupením. */
export const WORK_QUEUE_BUILDING_DP_TAB_IDS: readonly WorkQueueTabId[] = [
  'zafuk',
  'provareni',
] as const;

export function isWorkQueueBuildingDpTab(tab: WorkQueueTabId): boolean {
  return (WORK_QUEUE_BUILDING_DP_TAB_IDS as readonly string[]).includes(tab);
}

export type WorkQueueBuildingInput = {
  id?: string | null;
  status?: string | null;
  statusAcq?: string | null;
  /** Denormalizované Souhlas / smlouva z object_acquisition (sync ze smluv KN). */
  souhlasSmlouva?: string | null;
  /**
   * True = potvrzený podpis akviziční smlouvy (`souhlas` podepsana/archivovana).
   * null/undefined = neznámo → fail-closed (ve frontě), pokud ani souhlasSmlouva není Podepsaná.
   */
  acquisitionSigned?: boolean | null;
  /**
   * True = nahraný dokument akvizice u smluv (document_key / Drive / papírový sken).
   * null/undefined/false = chybí → fail-closed (ve frontě).
   */
  acquisitionDocumentUploaded?: boolean | null;
  /** Typ budovy (PRIPOJENA / BABOVKA) — výkres Akvizice: Podepsat / Zamítnutá. */
  budovaBuildingType?: string | null;
  /** Paralelní milníky BUDOVA (`metadata.budovaMilestones`). */
  budovaMilestones?: BudovaMilestonesFlags | null;
  unifiedHpCount?: number | null;
  /** Počet digitálních cest ze spec — gates fronta provaření (Svařený). */
  digitalniCesta?: number | null;
};

export type WorkQueueRozvadecInput = {
  id?: string | null;
  status?: string | null;
};

export type WorkQueuePolygonStats = {
  buildingCount: number | null;
  /** Budovy matched záložky K závrtu. */
  zavrtReadyCount: number | null;
  /** Budovy matched záložky K odbočení. */
  odboceniReadyCount: number | null;
  /** Budovy matched záložky VR. */
  vrReadyCount: number | null;
  /** Budovy matched záložky Digitální cesta. */
  digitalniCestaReadyCount: number | null;
  /** Budovy s pozitivní akvizicí (Uzavřeno / podepsaný souhlas) = řádky záložky Akvizice. */
  acquisitionOpenCount: number | null;
  acquisitionStatusCount: number | null;
  zafukReadyCount: number | null;
  weldReadyCount: number | null;
  /** Rozvaděče matched záložky Rozvaděče k postavení. */
  rozvadecPostaveniReadyCount: number | null;
  rozvadecZafukReadyCount: number | null;
  rozvadecWeldReadyCount: number | null;
};

/**
 * Krátké hlavičky sloupců na seznamu polygonů Fronta práce
 * (tab labels jsou delší; u milníků / postavení stačí zkratka).
 */
export const WORK_QUEUE_POLYGON_LIST_COLUMN_LABELS: Record<WorkQueueTabId, string> = {
  budovy: 'Budovy',
  'k-zavrtu': 'Závrt',
  'k-odboceni': 'Odbočka',
  vr: 'VR',
  'digitalni-cesta': 'Dig. cesta',
  akvizice: 'Akvizice',
  zafuk: WORK_QUEUE_TAB_LABELS.zafuk,
  provareni: WORK_QUEUE_TAB_LABELS.provareni,
  'rozvadec-postaveni': 'Postavení',
  'rozvadec-zafuk': WORK_QUEUE_TAB_LABELS['rozvadec-zafuk'],
  'rozvadec-provareni': WORK_QUEUE_TAB_LABELS['rozvadec-provareni'],
};

export function emptyWorkQueueStats(): WorkQueuePolygonStats {
  return {
    buildingCount: null,
    zavrtReadyCount: null,
    odboceniReadyCount: null,
    vrReadyCount: null,
    digitalniCestaReadyCount: null,
    acquisitionOpenCount: null,
    acquisitionStatusCount: null,
    zafukReadyCount: null,
    weldReadyCount: null,
    rozvadecPostaveniReadyCount: null,
    rozvadecZafukReadyCount: null,
    rozvadecWeldReadyCount: null,
  };
}

/** Status ACQ = Uzavřeno / Uzavřená (case-insensitive, bez diakritiky). */
export function isAcquisitionStatusClosedForQueue(statusAcq: string | null | undefined): boolean {
  if (isAcquisitionClosed(statusAcq)) return true;
  return normalizeAcquisitionText(statusAcq) === 'uzavrena';
}

export function isAcquisitionSouhlasPodepsana(souhlasSmlouva: string | null | undefined): boolean {
  return (
    normalizeAcquisitionText(souhlasSmlouva)
    === normalizeAcquisitionText(ACQUISITION_SOUHLAS_SMLUVA.PODEPSANA)
  );
}

export function isAcquisitionContractStatusSigned(status: string | null | undefined): boolean {
  const s = (status ?? '').trim().toLowerCase();
  return s === 'podepsana' || s === 'archivovana';
}

/** Nahraný dokument souhlasu: MinIO klíč, Drive, nebo papírový sken (slot 4). */
export function hasAcquisitionDocumentEvidence(opts: {
  documentKey?: string | null;
  driveFileId?: string | null;
  paperUploaded?: boolean | null;
}): boolean {
  if ((opts.documentKey ?? '').trim()) return true;
  if ((opts.driveFileId ?? '').trim()) return true;
  return opts.paperUploaded === true;
}

/** Budova má vyplněný Status ACQ (jakýkoli kanonický / volný text). */
export function hasAcquisitionStatus(statusAcq: string | null | undefined): boolean {
  return (statusAcq ?? '').trim().length > 0;
}

export function hasSignedAcquisition(building: WorkQueueBuildingInput): boolean {
  if (building.acquisitionSigned === true) return true;
  return isAcquisitionSouhlasPodepsana(building.souhlasSmlouva);
}

export function hasUploadedAcquisitionDocument(building: WorkQueueBuildingInput): boolean {
  return building.acquisitionDocumentUploaded === true;
}

/**
 * Hotová akvizice (mimo frontu): Uzavřeno + podepsaná smlouva + nahraný dokument.
 * Nejistý podpis / upload → není hotová.
 */
export function isAcquisitionWorkComplete(building: WorkQueueBuildingInput): boolean {
  return (
    isAcquisitionStatusClosedForQueue(building.statusAcq)
    && hasSignedAcquisition(building)
    && hasUploadedAcquisitionDocument(building)
  );
}

/**
 * Budova je v akviziční frontě, když akvizice není hotová.
 * Uzavřeno bez podpisu / dokumentu patří do fronty, ne do Chybí.
 */
export function isBuildingInAcquisition(building: WorkQueueBuildingInput): boolean {
  return !isAcquisitionWorkComplete(building);
}

/**
 * Pozitivní akvizice: Status ACQ = Uzavřeno/Uzavřená, nebo podepsaný akviziční souhlas
 * (`acquisitionSigned` / Souhlas–smlouva Podepsaná). Stejná množina jako zelená (OK)
 * barva akvizice na mapě. Fail-closed: neznámý / rozjednaný / Nesouhlas → není pozitivní.
 */
export function hasPositiveAcquisition(building: WorkQueueBuildingInput): boolean {
  return (
    isAcquisitionStatusClosedForQueue(building.statusAcq)
    || hasSignedAcquisition(building)
  );
}

/** Výkres Akvizice: Uzavřeno ∨ podpis ∨ nahraný dokument. */
export function isAcquisitionComplete(building: WorkQueueBuildingInput): boolean {
  return (
    isAcquisitionStatusClosedForQueue(building.statusAcq)
    || hasSignedAcquisition(building)
    || hasUploadedAcquisitionDocument(building)
  );
}

export const ACQUISITION_DRAWING_COLORS = {
  NONE: '#ffffff',
  REJECTED: '#000000',
  SIGNED: ACQUISITION_MAP_COLORS.OK,
} as const;

export type AcquisitionDrawingBuildingTone = 'none' | 'rejected' | 'signed';

/** Výkres Akvizice: zelená = uzavřeno, černá = zamítnuto, jinak bílá (Status ACQ only). */
export function resolveAcquisitionDrawingBuildingTone(
  building: WorkQueueBuildingInput,
): AcquisitionDrawingBuildingTone {
  if (isAkviziceDrawingSignedPair({ statusAcq: building.statusAcq })) return 'signed';
  if (isAkviziceDrawingRejectedPair({ statusAcq: building.statusAcq })) return 'rejected';
  return 'none';
}

export function getAcquisitionDrawingBuildingColor(building: WorkQueueBuildingInput): string {
  const tone = resolveAcquisitionDrawingBuildingTone(building);
  if (tone === 'signed') return ACQUISITION_DRAWING_COLORS.SIGNED;
  if (tone === 'rejected') return ACQUISITION_DRAWING_COLORS.REJECTED;
  return ACQUISITION_DRAWING_COLORS.NONE;
}

/** Připravená / čekající na záfuk: Připojené plasty nebo Připravená bábovka (ještě ne Zafouknutý). */
export function isBuildingReadyForZafuk(status: string | null | undefined): boolean {
  return (
    isSamePhase(status, PHASE_CODE.BUDOVA_PRIPOJENE_PLASTY)
    || isSamePhase(status, PHASE_CODE.BUDOVA_POSTAVENA_BABOVKA)
  );
}

/**
 * Bábovka ready for záfuk: lineární Připravená bábovka, nebo oba milníky hotové
 * (Odbočeno + Připravená bábovka) ještě na Nepřipojený.
 */
export function isBabovkaReadyForZafuk(building: WorkQueueBuildingInput): boolean {
  if (resolveWorkQueueBuildingType(building) !== 'BABOVKA') return false;
  if (isBuildingReadyForZafuk(building.status)) return true;
  if (!isBudovaNepripojenyPhase(building.status)) return false;
  const ctx = workQueueMilestoneCtx(building);
  return (
    isMilestoneComplete('BABOVKA', 'ODBOCENO', ctx)
    && isMilestoneComplete('BABOVKA', 'POSTAVENA_BABOVKA', ctx)
  );
}

/**
 * Čeká na provaření: Zafouknutý s následující fází Svařený (BUDOVA.2) a digitální cesta > 0.
 * BUDOVA.3 končí záfukem; Připojená bez digitální cesty také končí záfukem.
 */
export function isBuildingWaitingForWeld(
  status: string | null | undefined,
  digitalniCesta?: number | null,
): boolean {
  const code = (status ?? '').trim();
  if (!code || !isSamePhase(code, PHASE_CODE.BUDOVA_ZAFOUKNUTO)) return false;
  if (!budovaRequiresSvarPhase(digitalniCesta)) return false;
  return getNextPhaseCodes('BUDOVA', code).some((next) => isSamePhase(next, PHASE_CODE.BUDOVA_SVARENY));
}

/** Rozvaděč čeká na postavení: Nezačaté (ještě ne Postavený). */
export function isRozvadecReadyForPostaveni(status: string | null | undefined): boolean {
  return isSamePhase(status, PHASE_CODE.ROZVADEC_NEZACATE);
}

/** Rozvaděč čeká na záfuk: Postavený (ještě ne Zafouknutý). */
export function isRozvadecReadyForZafuk(status: string | null | undefined): boolean {
  return isSamePhase(status, PHASE_CODE.ROZVADEC_POSTAVENY);
}

/** Rozvaděč čeká na provaření: Zafouknutý s následující fází Provařené. */
export function isRozvadecWaitingForWeld(status: string | null | undefined): boolean {
  const code = (status ?? '').trim();
  if (!code || !isSamePhase(code, PHASE_CODE.ROZVADEC_ZAFOUKNUTO_SLOUPEK)) return false;
  return getNextPhaseCodes('ROZVADEC', code).some((next) =>
    isSamePhase(next, PHASE_CODE.ROZVADEC_PROVARENE),
  );
}

export function isWorkQueueRozvadecMatch(
  rozvadec: WorkQueueRozvadecInput,
  tab: WorkQueueTabId,
): boolean {
  switch (tab) {
    case 'rozvadec-postaveni':
      return isRozvadecReadyForPostaveni(rozvadec.status);
    case 'rozvadec-zafuk':
      return isRozvadecReadyForZafuk(rozvadec.status);
    case 'rozvadec-provareni':
      return isRozvadecWaitingForWeld(rozvadec.status);
    default:
      return false;
  }
}

/** Budova má nahrané committed unified HP (ne ruční spec / ROP). */
export function buildingHasUnifiedHp(unifiedHpCount: number | null | undefined): boolean {
  return unifiedHpCount != null && Number.isFinite(unifiedHpCount);
}

export function resolveWorkQueueBuildingType(
  building: WorkQueueBuildingInput,
): BudovaBuildingType | null {
  const raw = (building.budovaBuildingType ?? '').trim();
  return isBudovaBuildingType(raw) ? raw : null;
}

function workQueueMilestoneCtx(building: WorkQueueBuildingInput) {
  return {
    milestones: building.budovaMilestones ?? null,
    currentStatus: building.status,
  };
}

function resolveWorkQueueHp(building: WorkQueueBuildingInput): number | null {
  const hp = building.unifiedHpCount;
  if (hp == null || !Number.isFinite(Number(hp))) return null;
  return Number(hp);
}

/** Scope pro milníkové záložky — mimo scope nepatří do matched ani Chybí. */
export function isWorkQueueBuildingInScope(
  building: WorkQueueBuildingInput,
  tab: WorkQueueTabId,
): boolean {
  const type = resolveWorkQueueBuildingType(building);
  const hp = resolveWorkQueueHp(building);
  switch (tab) {
    case 'k-zavrtu':
      return type === 'PRIPOJENA';
    case 'k-odboceni':
      return type === 'PRIPOJENA' || type === 'BABOVKA';
    case 'vr':
      return type === 'PRIPOJENA' && hp != null && isVsoHp(hp);
    case 'digitalni-cesta':
      return type === 'PRIPOJENA' && hp != null && isMsoHp(hp);
    default:
      return true;
  }
}

export function isWorkQueueBuildingMatch(
  building: WorkQueueBuildingInput,
  tab: WorkQueueTabId,
): boolean {
  switch (tab) {
    case 'budovy':
      return true;
    case 'akvizice':
      return hasPositiveAcquisition(building);
    case 'zafuk':
      if (isBudovaZrusenaPhase(building.status)) return false;
      return isBuildingReadyForZafuk(building.status) || isBabovkaReadyForZafuk(building);
    case 'provareni':
      if (isBudovaZrusenaPhase(building.status)) return false;
      return isBuildingWaitingForWeld(building.status, building.digitalniCesta);
    case 'k-zavrtu': {
      if (!isWorkQueueBuildingInScope(building, tab)) return false;
      if (!isBudovaNepripojenyPhase(building.status)) return false;
      return !isMilestoneComplete('PRIPOJENA', 'ZAVRTANY', workQueueMilestoneCtx(building));
    }
    case 'k-odboceni': {
      if (!isWorkQueueBuildingInScope(building, tab)) return false;
      if (!isBudovaNepripojenyPhase(building.status)) return false;
      const type = resolveWorkQueueBuildingType(building);
      if (!type) return false;
      return !isMilestoneComplete(type, 'ODBOCENO', workQueueMilestoneCtx(building));
    }
    case 'vr': {
      if (!isWorkQueueBuildingInScope(building, tab)) return false;
      if (!isBudovaNepripojenyPhase(building.status)) return false;
      return !isMilestoneComplete('PRIPOJENA', 'ZRIZENE_VR', workQueueMilestoneCtx(building));
    }
    case 'digitalni-cesta': {
      if (!isWorkQueueBuildingInScope(building, tab)) return false;
      if (!isBudovaNepripojenyPhase(building.status)) return false;
      return !isMilestoneComplete('PRIPOJENA', 'ZRIZENE_VR', workQueueMilestoneCtx(building));
    }
    default:
      return false;
  }
}

/**
 * Milník fronty už splněn → objekt nepatří do Chybí.
 * Budovy / Akvizice nemají workflow-milník exclusion (Chybí = doplněk matched).
 * Milníkové záložky: hotový milník nebo opuštěná fáze Nepřipojený.
 */
export function isWorkQueueBuildingMilestoneComplete(
  building: WorkQueueBuildingInput,
  tab: WorkQueueTabId,
): boolean {
  switch (tab) {
    case 'zafuk':
      // Zrušený = hotovo / vyloučeno (už nepatří do matched ani Chybí).
      if (isBudovaZrusenaPhase(building.status)) return true;
      return hasReachedWorkflowMilestone(building.status, PHASE_CODE.BUDOVA_ZAFOUKNUTO);
    case 'provareni':
      if (isBudovaZrusenaPhase(building.status)) return true;
      if (hasReachedWorkflowMilestone(building.status, PHASE_CODE.BUDOVA_SVARENY)) return true;
      // BUDOVA.3 / Připojená bez digitální cesty končí záfukem — provaření už nechybí.
      return (
        hasReachedWorkflowMilestone(building.status, PHASE_CODE.BUDOVA_ZAFOUKNUTO)
        && !isBuildingWaitingForWeld(building.status, building.digitalniCesta)
      );
    case 'k-zavrtu':
      if (!isBudovaNepripojenyPhase(building.status)) return true;
      return isMilestoneComplete('PRIPOJENA', 'ZAVRTANY', workQueueMilestoneCtx(building));
    case 'k-odboceni': {
      if (!isBudovaNepripojenyPhase(building.status)) return true;
      const type = resolveWorkQueueBuildingType(building);
      if (!type) return false;
      return isMilestoneComplete(type, 'ODBOCENO', workQueueMilestoneCtx(building));
    }
    case 'vr':
    case 'digitalni-cesta':
      if (!isBudovaNepripojenyPhase(building.status)) return true;
      return isMilestoneComplete('PRIPOJENA', 'ZRIZENE_VR', workQueueMilestoneCtx(building));
    default:
      return false;
  }
}

export function isWorkQueueRozvadecMilestoneComplete(
  rozvadec: WorkQueueRozvadecInput,
  tab: WorkQueueTabId,
): boolean {
  switch (tab) {
    case 'rozvadec-postaveni':
      return hasReachedWorkflowMilestone(rozvadec.status, PHASE_CODE.ROZVADEC_POSTAVENY);
    case 'rozvadec-zafuk':
      return hasReachedWorkflowMilestone(
        rozvadec.status,
        PHASE_CODE.ROZVADEC_ZAFOUKNUTO_SLOUPEK,
      );
    case 'rozvadec-provareni':
      return hasReachedWorkflowMilestone(rozvadec.status, PHASE_CODE.ROZVADEC_PROVARENE);
    default:
      return false;
  }
}

/** Chybí = milník ještě chybí a objekt není v aktuální frontě (matched). */
export function isWorkQueueBuildingMissing(
  building: WorkQueueBuildingInput,
  tab: WorkQueueTabId,
): boolean {
  if (isWorkQueueBuildingMatch(building, tab)) return false;
  if (isWorkQueueBuildingMilestoneComplete(building, tab)) return false;
  // Milníkové záložky: Chybí jen v type/HP scope (cizí typy nezahlcují).
  if ((WORK_QUEUE_MILESTONE_TAB_IDS as readonly string[]).includes(tab)) {
    return isWorkQueueBuildingInScope(building, tab);
  }
  return true;
}

export function isWorkQueueRozvadecMissing(
  rozvadec: WorkQueueRozvadecInput,
  tab: WorkQueueTabId,
): boolean {
  if (isWorkQueueRozvadecMatch(rozvadec, tab)) return false;
  if (isWorkQueueRozvadecMilestoneComplete(rozvadec, tab)) return false;
  return true;
}

export function partitionWorkQueue<T>(
  all: readonly T[],
  predicate: (item: T) => boolean,
): { matched: T[]; missing: T[] } {
  const matched: T[] = [];
  const missing: T[] = [];
  for (const item of all) {
    if (predicate(item)) matched.push(item);
    else missing.push(item);
  }
  return { matched, missing };
}

export type WorkQueuePartition<T> = {
  /** Připravené k práci (aktuální fronta). */
  matched: T[];
  /** Chybí — milník ještě není a objekt není v matched. */
  missing: T[];
  /** Hotovo — milník už dosažen (mimo matched i Chybí). */
  done: T[];
};

export function partitionWorkQueueByTab<T extends WorkQueueBuildingInput>(
  all: readonly T[],
  tab: WorkQueueTabId,
): WorkQueuePartition<T> {
  const matched: T[] = [];
  const missing: T[] = [];
  const done: T[] = [];
  for (const item of all) {
    if (isWorkQueueBuildingMatch(item, tab)) matched.push(item);
    else if (isWorkQueueBuildingMissing(item, tab)) missing.push(item);
    else if (isWorkQueueBuildingMilestoneComplete(item, tab)) done.push(item);
  }
  return { matched, missing, done };
}

export function partitionWorkQueueRozvadecByTab<T extends WorkQueueRozvadecInput>(
  all: readonly T[],
  tab: WorkQueueTabId,
): WorkQueuePartition<T> {
  const matched: T[] = [];
  const missing: T[] = [];
  const done: T[] = [];
  for (const item of all) {
    if (isWorkQueueRozvadecMatch(item, tab)) matched.push(item);
    else if (isWorkQueueRozvadecMissing(item, tab)) missing.push(item);
    else if (isWorkQueueRozvadecMilestoneComplete(item, tab)) done.push(item);
  }
  return { matched, missing, done };
}

/**
 * Počty budov + rozvaděčů po frontách (matched = stejné predikáty jako záložky).
 * `rozvadece` = null/undefined → fail-closed (rozvaděčové agregáty null, UI kreslí —),
 * prázdné pole → 0.
 */
export function aggregateWorkQueueCounts(
  buildings: readonly WorkQueueBuildingInput[],
  rozvadece?: readonly WorkQueueRozvadecInput[] | null,
): WorkQueuePolygonStats {
  let acquisitionOpenCount = 0;
  let acquisitionStatusCount = 0;
  let zavrtReadyCount = 0;
  let odboceniReadyCount = 0;
  let vrReadyCount = 0;
  let digitalniCestaReadyCount = 0;
  let zafukReadyCount = 0;
  let weldReadyCount = 0;
  for (const building of buildings) {
    if (hasAcquisitionStatus(building.statusAcq)) acquisitionStatusCount += 1;
    if (isWorkQueueBuildingMatch(building, 'akvizice')) acquisitionOpenCount += 1;
    if (isWorkQueueBuildingMatch(building, 'k-zavrtu')) zavrtReadyCount += 1;
    if (isWorkQueueBuildingMatch(building, 'k-odboceni')) odboceniReadyCount += 1;
    if (isWorkQueueBuildingMatch(building, 'vr')) vrReadyCount += 1;
    if (isWorkQueueBuildingMatch(building, 'digitalni-cesta')) digitalniCestaReadyCount += 1;
    if (isWorkQueueBuildingMatch(building, 'zafuk')) zafukReadyCount += 1;
    if (isWorkQueueBuildingMatch(building, 'provareni')) weldReadyCount += 1;
  }
  let rozvadecPostaveniReadyCount: number | null = null;
  let rozvadecZafukReadyCount: number | null = null;
  let rozvadecWeldReadyCount: number | null = null;
  if (rozvadece != null) {
    rozvadecPostaveniReadyCount = 0;
    rozvadecZafukReadyCount = 0;
    rozvadecWeldReadyCount = 0;
    for (const rozvadec of rozvadece) {
      if (isWorkQueueRozvadecMatch(rozvadec, 'rozvadec-postaveni')) {
        rozvadecPostaveniReadyCount += 1;
      }
      if (isWorkQueueRozvadecMatch(rozvadec, 'rozvadec-zafuk')) rozvadecZafukReadyCount += 1;
      if (isWorkQueueRozvadecMatch(rozvadec, 'rozvadec-provareni')) rozvadecWeldReadyCount += 1;
    }
  }
  return {
    buildingCount: buildings.length,
    zavrtReadyCount,
    odboceniReadyCount,
    vrReadyCount,
    digitalniCestaReadyCount,
    acquisitionOpenCount,
    acquisitionStatusCount,
    zafukReadyCount,
    weldReadyCount,
    rozvadecPostaveniReadyCount,
    rozvadecZafukReadyCount,
    rozvadecWeldReadyCount,
  };
}

/** Fail-closed display: chybějící agregát → null (UI kreslí —). */
export function formatWorkQueueCount(value: number | null | undefined): string | null {
  if (value == null || !Number.isFinite(value)) return null;
  return String(value);
}

/**
 * Akvizice na listu: jedno číslo = počet budov s pozitivní akvizicí (řádky záložky Akvizice).
 * Nikdy poměr ani placeholder (`12 / 40`, `12 / ...`) — jmenovatel `acquisitionStatusCount`
 * sem nepatří (často 0 → historicky `N / ...`).
 */
export function formatWorkQueueAcquisitionCell(
  openCount: number | null | undefined,
): string | null {
  return formatWorkQueueCount(openCount);
}

export function parseWorkQueueTab(value: unknown): WorkQueueTabId {
  const raw = typeof value === 'string' ? value.trim() : '';
  // Legacy `?tab=hp` → Budovy (HP záložka odstraněna).
  if (raw === 'hp') return WORK_QUEUE_DEFAULT_TAB;
  return (WORK_QUEUE_TAB_IDS as readonly string[]).includes(raw)
    ? (raw as WorkQueueTabId)
    : WORK_QUEUE_DEFAULT_TAB;
}
